// @ts-nocheck
/**
 * Enterprise Settings Dashboard
 * Comprehensive enterprise management interface following Carbon Design System
 */

import React, { useState, useEffect } from 'react';
import {
  Content,
  Grid,
  Column,
  Stack,
  Heading,
  Tile,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Tag,
  InlineNotification,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Modal,
  TextInput,
  Select,
  SelectItem,
  Checkbox,
  FormGroup,
  Loading,
  ProgressBar,
  Badge,
  Theme,
} from '@carbon/react';
import SecuritySettings from '../../components/security/SecuritySettings';
import {
  Enterprise,
  UserMultiple,
  Security,
  Analytics,
  Settings,
  Add,
  Edit,
  View,
  Download,
  Upload,
  CheckmarkFilled,
  Warning,
  Email,
  Calendar,
} from '@carbon/icons-react';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  status: 'active' | 'pending' | 'inactive';
  lastLogin: string;
  progress: number;
}

interface Department {
  id: string;
  name: string;
  memberCount: number;
  avgProgress: number;
  head: string;
}

const EnterpriseSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSSOModal, setShowSSOModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [inviteForm, setInviteForm] = useState({
    emails: '',
    role: 'employee',
    department: '',
    sendWelcome: true,
  });

  // Mock data - would come from API in real implementation
  useEffect(() => {
    const loadData = async () => {
      // Simulate API call
      setTimeout(() => {
        setTeamMembers([
          {
            id: '1',
            name: 'Sarah Johnson',
            email: 'sarah.johnson@company.com',
            role: 'Manager',
            department: 'Engineering',
            status: 'active',
            lastLogin: '2025-09-22',
            progress: 85,
          },
          {
            id: '2',
            name: 'Mike Chen',
            email: 'mike.chen@company.com',
            role: 'Developer',
            department: 'Engineering',
            status: 'active',
            lastLogin: '2025-09-21',
            progress: 92,
          },
          {
            id: '3',
            name: 'Lisa Rodriguez',
            email: 'lisa.rodriguez@company.com',
            role: 'Designer',
            department: 'Product',
            status: 'pending',
            lastLogin: 'Never',
            progress: 0,
          },
        ]);

        setDepartments([
          { id: '1', name: 'Engineering', memberCount: 25, avgProgress: 78, head: 'Sarah Johnson' },
          { id: '2', name: 'Product', memberCount: 12, avgProgress: 82, head: 'John Smith' },
          { id: '3', name: 'Sales', memberCount: 18, avgProgress: 91, head: 'Emma Davis' },
          { id: '4', name: 'Marketing', memberCount: 8, avgProgress: 86, head: 'Alex Kumar' },
        ]);

        setLoading(false);
      }, 1500);
    };

    loadData();
  }, []);

  const handleInviteTeamMembers = async () => {
    // Simulate team member invitation
    console.log('Inviting team members:', inviteForm);
    setShowInviteModal(false);
    setInviteForm({ emails: '', role: 'employee', department: '', sendWelcome: true });
  };

  const teamMemberHeaders = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'role', header: 'Role' },
    { key: 'department', header: 'Department' },
    { key: 'status', header: 'Status' },
    { key: 'progress', header: 'Training Progress' },
    { key: 'actions', header: 'Actions' },
  ];

  const departmentHeaders = [
    { key: 'name', header: 'Department' },
    { key: 'head', header: 'Department Head' },
    { key: 'memberCount', header: 'Members' },
    { key: 'avgProgress', header: 'Avg Progress' },
    { key: 'actions', header: 'Actions' },
  ];

  if (loading) {
    return (
      <Content style={{ padding: '2rem' }}>
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <Loading size="large" />
          <p style={{ marginTop: '1rem' }}>Loading enterprise dashboard...</p>
        </div>
      </Content>
    );
  }

  return (
    <Theme theme="g100">
      <Content style={{ padding: '2rem' }}>
        <Grid>
          <Column span={16}>
            <Stack gap={6}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-05)' }}>
                  <Enterprise size={32} />
                  <div>
                    <Heading size="lg">Enterprise Settings</Heading>
                    <p className="cds--body-short-01" style={{ color: 'var(--cds-text-secondary)', margin: 0 }}>
                      Manage your organization's team, training, and integration settings
                    </p>
                  </div>
                </div>
                <Button kind="primary" renderIcon={Add} onClick={() => setShowInviteModal(true)}>
                  Invite Team Members
                </Button>
              </div>

              {/* Overview Cards */}
              <Grid>
                <Column span={4}>
                  <Tile style={{ padding: 'var(--cds-spacing-06)', height: '120px' }}>
                    <Stack gap={2}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
                        <UserMultiple size={20} />
                        <span className="cds--label-01" style={{ color: 'var(--cds-text-secondary)' }}>
                          Total Members
                        </span>
                      </div>
                      <Heading size="xl">63</Heading>
                      <Tag type="green" size="sm">
                        +5 this month
                      </Tag>
                    </Stack>
                  </Tile>
                </Column>
                <Column span={4}>
                  <Tile style={{ padding: 'var(--cds-spacing-06)', height: '120px' }}>
                    <Stack gap={2}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
                        <Analytics size={20} />
                        <span className="cds--label-01" style={{ color: 'var(--cds-text-secondary)' }}>
                          Avg Progress
                        </span>
                      </div>
                      <Heading size="xl">84%</Heading>
                      <Tag type="blue" size="sm">
                        ↑ 12% vs last month
                      </Tag>
                    </Stack>
                  </Tile>
                </Column>
                <Column span={4}>
                  <Tile style={{ padding: 'var(--cds-spacing-06)', height: '120px' }}>
                    <Stack gap={2}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
                        <Security size={20} />
                        <span className="cds--label-01" style={{ color: 'var(--cds-text-secondary)' }}>
                          SSO Status
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
                        <CheckmarkFilled size={16} style={{ color: 'var(--cds-support-success)' }} />
                        <span>Active</span>
                      </div>
                      <Button kind="ghost" size="sm" onClick={() => setShowSSOModal(true)}>
                        Configure
                      </Button>
                    </Stack>
                  </Tile>
                </Column>
                <Column span={4}>
                  <Tile style={{ padding: 'var(--cds-spacing-06)', height: '120px' }}>
                    <Stack gap={2}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
                        <Settings size={20} />
                        <span className="cds--label-01" style={{ color: 'var(--cds-text-secondary)' }}>
                          Departments
                        </span>
                      </div>
                      <Heading size="xl">{departments.length}</Heading>
                      <Tag type="purple" size="sm">
                        All configured
                      </Tag>
                    </Stack>
                  </Tile>
                </Column>
              </Grid>

              {/* Main Content Tabs */}
              <Tabs selectedIndex={activeTab} onChange={evt => setActiveTab(evt.selectedIndex)}>
                <TabList>
                  <Tab>Team Management</Tab>
                  <Tab>Departments</Tab>
                  <Tab>Security Settings</Tab>
                  <Tab>SSO Integration</Tab>
                  <Tab>Training Analytics</Tab>
                  <Tab>Billing & Usage</Tab>
                </TabList>
                <TabPanels>
                  {/* Team Management Tab */}
                  <TabPanel>
                    <Stack gap={4}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Heading size="md">Team Members</Heading>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button kind="secondary" size="md" renderIcon={Upload}>
                            Bulk Import
                          </Button>
                          <Button kind="secondary" size="md" renderIcon={Download}>
                            Export
                          </Button>
                        </div>
                      </div>

                      <DataTable
                        rows={teamMembers.map(member => ({
                          ...member,
                          status: (
                            <Tag
                              type={
                                member.status === 'active' ? 'green' : member.status === 'pending' ? 'yellow' : 'red'
                              }
                              size="sm"
                            >
                              {member.status}
                            </Tag>
                          ),
                          progress: (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--cds-spacing-03)',
                                minWidth: '120px',
                              }}
                            >
                              <ProgressBar
                                value={member.progress}
                                max={100}
                                label={`Member progress: ${member.progress}%`}
                                helperText=""
                              />
                              <span className="cds--helper-text-01" style={{ minWidth: '30px' }}>
                                {member.progress}%
                              </span>
                            </div>
                          ),
                          actions: (
                            <div style={{ display: 'flex', gap: 'var(--cds-spacing-02)' }}>
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={View}
                                iconDescription="View profile"
                                aria-label={`View profile for ${member.name}`}
                              />
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={Edit}
                                iconDescription="Edit user"
                                aria-label={`Edit user ${member.name}`}
                              />
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={Email}
                                iconDescription="Send message"
                                aria-label={`Send message to ${member.name}`}
                              />
                            </div>
                          ),
                        }))}
                        headers={teamMemberHeaders}
                      >
                        {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                          <TableContainer>
                            <Table {...getTableProps()}>
                              <TableHead>
                                <TableRow>
                                  {headers.map(header => (
                                    <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rows.map(row => (
                                  <TableRow {...getRowProps({ row })}>
                                    {row.cells.map(cell => (
                                      <TableCell key={cell.id}>{cell.value}</TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </DataTable>
                    </Stack>
                  </TabPanel>

                  {/* Departments Tab */}
                  <TabPanel>
                    <Stack gap={4}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Heading size="md">Department Structure</Heading>
                        <Button kind="primary" size="md" renderIcon={Add}>
                          Add Department
                        </Button>
                      </div>

                      <DataTable
                        rows={departments.map(dept => ({
                          ...dept,
                          avgProgress: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
                              <ProgressBar
                                value={dept.avgProgress}
                                max={100}
                                label={`Department progress: ${dept.avgProgress}%`}
                                helperText=""
                              />
                              <span className="cds--helper-text-01">{dept.avgProgress}%</span>
                            </div>
                          ),
                          actions: (
                            <div style={{ display: 'flex', gap: 'var(--cds-spacing-02)' }}>
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={View}
                                iconDescription="View department"
                                aria-label={`View ${dept.name} department`}
                              />
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={Edit}
                                iconDescription="Edit department"
                                aria-label={`Edit ${dept.name} department`}
                              />
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                renderIcon={Analytics}
                                iconDescription="Analytics"
                                aria-label={`View analytics for ${dept.name}`}
                              />
                            </div>
                          ),
                        }))}
                        headers={departmentHeaders}
                      >
                        {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                          <TableContainer>
                            <Table {...getTableProps()}>
                              <TableHead>
                                <TableRow>
                                  {headers.map(header => (
                                    <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rows.map(row => (
                                  <TableRow {...getRowProps({ row })}>
                                    {row.cells.map(cell => (
                                      <TableCell key={cell.id}>{cell.value}</TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </DataTable>
                    </Stack>
                  </TabPanel>

                  {/* Security Settings Tab - Epic #87 */}
                  <TabPanel>
                    <Stack gap={4}>
                      <Heading size="md">Security Settings</Heading>
                      <SecuritySettings />
                    </Stack>
                  </TabPanel>

                  {/* SSO Integration Tab */}
                  <TabPanel>
                    <Stack gap={4}>
                      <Heading size="md">Single Sign-On Configuration</Heading>

                      <InlineNotification
                        kind="success"
                        title="SSO Active"
                        subtitle="Azure AD integration is configured and working properly"
                        hideCloseButton
                      />

                      <Grid>
                        <Column span={8}>
                          <Tile style={{ padding: 'var(--cds-spacing-06)' }}>
                            <Stack gap={3}>
                              <h4 className="cds--type-heading-03">Current Configuration</h4>
                              <div className="cds--body-short-01">
                                <p>
                                  <strong>Provider:</strong> Microsoft Azure AD
                                </p>
                                <p>
                                  <strong>Entity ID:</strong> sigma08-your-org
                                </p>
                                <p>
                                  <strong>Status:</strong>{' '}
                                  <Tag type="green" size="sm">
                                    Active
                                  </Tag>
                                </p>
                                <p>
                                  <strong>Users:</strong> 45 of 63 members using SSO
                                </p>
                              </div>
                              <Button kind="secondary" size="md">
                                Update Configuration
                              </Button>
                            </Stack>
                          </Tile>
                        </Column>
                        <Column span={8}>
                          <Tile style={{ padding: 'var(--cds-spacing-06)' }}>
                            <Stack gap={3}>
                              <h4 className="cds--type-heading-03">Security Settings</h4>
                              <Checkbox id="enforceSSO" labelText="Enforce SSO for all users" checked={true} disabled />
                              <Checkbox
                                id="jitProvisioning"
                                labelText="Just-in-time user provisioning"
                                checked={true}
                                disabled
                              />
                              <Checkbox
                                id="groupMapping"
                                labelText="Automatic group/role mapping"
                                checked={false}
                                disabled
                              />
                            </Stack>
                          </Tile>
                        </Column>
                      </Grid>
                    </Stack>
                  </TabPanel>

                  {/* Training Analytics Tab */}
                  <TabPanel>
                    <Stack gap={4}>
                      <Heading size="md">Training Analytics</Heading>

                      <InlineNotification
                        kind="info"
                        title="Analytics Overview"
                        subtitle="Training progress and engagement metrics for your organization"
                        hideCloseButton
                      />

                      <Grid>
                        <Column span={16}>
                          <Tile style={{ padding: 'var(--cds-spacing-06)' }}>
                            <Stack gap={3}>
                              <h4 className="cds--type-heading-03">Training Progress by Department</h4>
                              <div className="cds--body-short-01" style={{ color: 'var(--cds-text-secondary)' }}>
                                Detailed analytics dashboard would be implemented here with charts and metrics
                              </div>
                            </Stack>
                          </Tile>
                        </Column>
                      </Grid>
                    </Stack>
                  </TabPanel>

                  {/* Billing & Usage Tab */}
                  <TabPanel>
                    <Stack gap={4}>
                      <Heading size="md">Billing & Usage</Heading>

                      <InlineNotification
                        kind="warning"
                        title="Enterprise Plan"
                        subtitle="Your organization is on the Enterprise plan with 100 user licenses"
                        hideCloseButton
                      />

                      <Grid>
                        <Column span={8}>
                          <Tile style={{ padding: 'var(--cds-spacing-06)' }}>
                            <Stack gap={3}>
                              <h4 className="cds--type-heading-03">Current Usage</h4>
                              <div className="cds--body-short-01">
                                <p>
                                  <strong>Active Users:</strong> 63 of 100
                                </p>
                                <p>
                                  <strong>Storage Used:</strong> 2.1 GB of 10 GB
                                </p>
                                <p>
                                  <strong>API Calls:</strong> 15,432 this month
                                </p>
                              </div>
                            </Stack>
                          </Tile>
                        </Column>
                        <Column span={8}>
                          <Tile style={{ padding: 'var(--cds-spacing-06)' }}>
                            <Stack gap={3}>
                              <h4 className="cds--type-heading-03">Billing Information</h4>
                              <div className="cds--body-short-01">
                                <p>
                                  <strong>Plan:</strong> Enterprise
                                </p>
                                <p>
                                  <strong>Billing Cycle:</strong> Annual
                                </p>
                                <p>
                                  <strong>Next Payment:</strong> Dec 1, 2025
                                </p>
                              </div>
                              <Button kind="secondary" size="md">
                                Manage Billing
                              </Button>
                            </Stack>
                          </Tile>
                        </Column>
                      </Grid>
                    </Stack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Stack>
          </Column>
        </Grid>

        {/* Team Invitation Modal */}
        <Modal
          open={showInviteModal}
          modalHeading="Invite Team Members"
          primaryButtonText="Send Invitations"
          secondaryButtonText="Cancel"
          onRequestClose={() => setShowInviteModal(false)}
          onRequestSubmit={handleInviteTeamMembers}
        >
          <Stack gap={4}>
            <FormGroup legendText="">
              <TextInput
                id="inviteEmails"
                labelText="Email Addresses"
                placeholder="Enter email addresses separated by commas"
                value={inviteForm.emails}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setInviteForm({ ...inviteForm, emails: e.target.value })
                }
                helperText="Separate multiple email addresses with commas"
              />
            </FormGroup>

            <FormGroup legendText="">
              <Select
                id="inviteRole"
                labelText="Default Role"
                value={inviteForm.role}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setInviteForm({ ...inviteForm, role: e.target.value })
                }
              >
                <SelectItem value="employee" text="Employee" />
                <SelectItem value="manager" text="Manager" />
                <SelectItem value="admin" text="Administrator" />
              </Select>
            </FormGroup>

            <FormGroup legendText="">
              <Select
                id="inviteDepartment"
                labelText="Department"
                value={inviteForm.department}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setInviteForm({ ...inviteForm, department: e.target.value })
                }
              >
                <SelectItem value="" text="Select Department" />
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.name} text={dept.name} />
                ))}
              </Select>
            </FormGroup>

            <FormGroup legendText="">
              <Checkbox
                id="sendWelcome"
                labelText="Send welcome email with setup instructions"
                checked={inviteForm.sendWelcome}
                onChange={(checked: boolean) => setInviteForm({ ...inviteForm, sendWelcome: checked })}
              />
            </FormGroup>
          </Stack>
        </Modal>
      </Content>
    </Theme>
  );
};

export default EnterpriseSettings;
